import { isAxiosError, type AxiosResponse } from "axios";

/** The fields of a backend error body the client reads; anything may be missing or mistyped. */
export interface ApiErrorBody {
  message?: unknown;
  error_type?: unknown;
  error_data?: { [key: string]: unknown } | null;
}

/**
 * The HTTP response behind a failed API call.
 *
 * @returns the response, or undefined when the failure never reached the backend (a network drop)
 * or is not an axios error at all
 */
export function apiErrorResponse(
  error: unknown
): AxiosResponse<ApiErrorBody | undefined> | undefined {
  return isAxiosError<ApiErrorBody | undefined>(error)
    ? error.response
    : undefined;
}

/**
 * The message of a refused request (4xx), which the backend words for the user.
 *
 * A failure (5xx, no response) carries no such message and belongs with `globalErrorHandler`.
 */
export function clientErrorMessage(error: unknown): string | null {
  const response = apiErrorResponse(error);
  if (response === undefined || response.status < 400 || response.status >= 500)
    return null;

  const message = response.data?.message;
  return typeof message === "string" && message.length > 0 ? message : null;
}

/**
 * What to log a failed API call with: its HTTP status, undefined when the backend never answered,
 * and its message.
 *
 * SECURITY: never the error itself: an axios error carries its request config, whose headers hold
 * the session's bearer token.
 */
export function apiErrorLogFields(error: unknown): {
  status: number | undefined;
  message: string | undefined;
} {
  return {
    status: apiErrorResponse(error)?.status,
    message: error instanceof Error ? error.message : undefined,
  };
}

/**
 * A message to show for a failed API call: the backend's own wording when it sent one, else the
 * error's message, else `fallback`.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const message = apiErrorResponse(error)?.data?.message;
  if (typeof message === "string" && message.length > 0) return message;
  return error instanceof Error && error.message ? error.message : fallback;
}
